/**
 * Visitor-facing i18n. v1 scope: everything a venue visitor sees/hears
 * (landing, status pill, guidance HUD, picker, tours, voice prompts).
 * The Dashboard is a staff tool and stays English for now.
 *
 * Adding a language: add its code to LANGS, a dictionary below, and a BCP-47
 * voice tag in VOICE_LANG. Keys missing from a dictionary fall back to English.
 */

export type Lang = 'en' | 'es' | 'hi' | 'te' | 'fr' | 'de' | 'pt' | 'zh' | 'ja';

export const LANGS: Array<{ code: Lang; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'pt', label: 'Português' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'te', label: 'తెలుగు' },
  { code: 'zh', label: '中文' },
  { code: 'ja', label: '日本語' },
];

/** BCP-47 tags for SpeechSynthesis voices. */
const VOICE_LANG: Record<Lang, string> = {
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  de: 'de-DE',
  pt: 'pt-BR',
  hi: 'hi-IN',
  te: 'te-IN',
  zh: 'zh-CN',
  ja: 'ja-JP',
};

const STORAGE_KEY = 'navme.lang';

function detectLang(): Lang {
  const saved = localStorage.getItem(STORAGE_KEY) as Lang | null;
  if (saved && LANGS.some((l) => l.code === saved)) return saved;
  const nav = (navigator.language || 'en').slice(0, 2) as Lang;
  return LANGS.some((l) => l.code === nav) ? nav : 'en';
}

let current: Lang = detectLang();

export function getLang(): Lang {
  return current;
}

export function setLang(lang: Lang): void {
  current = lang;
  localStorage.setItem(STORAGE_KEY, lang);
}

export function voiceLang(): string {
  return VOICE_LANG[current];
}

/** Translate `key`, interpolating {placeholders} from params. */
export function t(key: string, params?: Record<string, string | number>): string {
  const template = DICTS[current][key] ?? DICTS.en[key] ?? key;
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, name: string) => String(params[name] ?? `{${name}}`));
}

type Dict = Record<string, string>;

const en: Dict = {
  'landing.explain':
    'Open your camera to start indoor AR navigation. Point your phone around the space to localize, pick a destination, and follow the arrows.',
  'landing.start': 'Start AR',
  'landing.checking': 'Checking device…',
  'landing.connecting': 'Connecting…',
  'landing.ready': 'Ready. Make sure you are inside the mapped space.',
  'state.notStarted': 'Not started',
  'state.permission': 'Camera permission needed',
  'state.localizing': 'Localizing…',
  'state.localized': 'Localized',
  'state.trackingWeak': 'Tracking weak',
  'state.lostTracking': 'Lost tracking',
  'state.relocalizing': 'Relocalizing…',
  'state.error': 'Error',
  'hud.toNextTurn': '{d} to next turn',
  'hud.straight': 'Straight ahead',
  'hud.turnAround': '↩️ Turn around',
  'voice.turnAround': 'Turn around — the route is behind you',
  'hud.turns': '{n} turns',
  'hud.turn1': '1 turn',
  'hud.eta': '≈{n} min to',
  'hud.floorUp': '⬆️ Head up the stairs',
  'hud.floorDown': '⬇️ Head down the stairs',
  'arrival.title': '🎉 You have arrived!',
  'arrival.again': 'Navigate somewhere else',
  'route.none': 'No route to {name} — cross-floor navigation needs a navmesh or stairs/elevator edges.',
  // Chat-triggered nav before localization finishes (en only — t() falls back).
  'route.waitingForLoc': "Still finding your position — I'll start guiding you to {name} the moment we're locked on.",
  'voice.start': 'Navigating to {name}, {m} meters',
  'voice.turn': 'Turn ahead in {m} meters',
  'voice.arrived': 'You have arrived at {name}',
  'voice.updated': 'Route updated',
  'voice.stairsUp': 'Take the stairs up ahead',
  'voice.stairsDown': 'Take the stairs down ahead',
  'voice.on': 'Voice guidance on',
  'tour.start': 'Starting tour: {name}, {n} stops',
  'tour.stop': 'Stop {i} of {n}: {name}',
  'tour.complete': 'Tour complete',
  'tour.next': '✓ {name} · next stop in a moment…',
  'prox.passing': "You're passing {name}",
  'picker.whereTo': 'Where to?',
  'picker.search': 'Search destinations…',
  'picker.stepFree': '♿ Step-free route (avoid stairs)',
  'picker.alerts': '🔔 Alert me when passing places',
  'picker.tours': 'Guided tours',
  'picker.dests': 'Destinations',
  'picker.offers': '🏷 Offers',
  'picker.products': '🛒 Products',
  'picker.none': 'No matches.',
  'picker.stops': '{n} stops',
  'ctrl.relocalize': 'Relocalize',
  'ctrl.change': 'Change destination',
  'ctrl.voice': 'Voice guidance',
  'ctrl.stop': 'Stop navigation',
  'loc.noPose': 'No pose found — point the camera at a mapped, feature-rich area',
  'loc.lowConf': 'Low confidence ({c}) — point at a distinctive, well-lit area',
  'link.open': 'Open in browser',
  'ctrl.explore': 'Explore places',
  'ctrl.park': 'Save parking spot',
  'park.saved': 'Parking spot saved — find it under \u201cWhere to?\u201d',
  'park.mySpot': 'My parking spot',
  'ctrl.concierge': 'Ask concierge',
  'concierge.title': 'Ask NavMe',
  'concierge.placeholder': 'e.g. \u201cnearest restroom?\u201d',
  'concierge.thinking': 'Thinking…',
  'concierge.go': 'Take me there',
  'park.arrivedPrompt': 'You have reached your car — delete the saved parking spot?',
  'ctrl.photo': 'Take photo',
  'ctrl.video': 'Record video',
  'ctrl.report': 'Send visit report',
  // Arrival auto-capture overlay + review sheet (en only — t() falls back).
  'cap.photo': '📸 Taking a photo…',
  'cap.recording': '🎥 Recording your visit',
  'cap.moveAround': 'move around to capture your surroundings',
  'cap.captured': '✓ Captured!',
  'cap.reviewBadge': 'YOUR VISIT',
  'cap.reviewTitle': 'Your capture at {name}',
  'cap.recordingChip': 'Recording · {s}s',
  'cap.snapshot': 'Snapshot',
  'cap.video': 'Recording',
  'cap.rate': 'Rate this spot',
  'cap.notes': 'Notes',
  'cap.notesPh': 'Add a note about this spot (optional).',
  'cap.save': 'Save',
  'cap.skip': 'Skip',
  'cap.saved': '✓ Thanks — saved with your visit report!',
  // Concierge agent v2 (en only — t() falls back).
  'concierge.goRoute': 'Take me on this route',
  'concierge.routeName': 'Your route',
  // Concierge v8 — chat-driven POI bookmarking (en only — t() falls back).
  'concierge.placeIt': '📍 Place it',
  'concierge.tapToPlace': 'Tap the spot on your screen to bookmark it.',
  'concierge.suggestionSaved': '✓ Spot saved — a staff member will review it.',
  // Localization coach (agent 2, en only — t() falls back).
  'coach.ahead': 'ahead of you',
  'coach.behind': 'behind you',
  'coach.left': 'to your left',
  'coach.right': 'to your right',
  'coach.thatArea': 'that area',
  'coach.zone': 'Tracking is struggling — point the camera toward {landmark}, about {m} m {dir}, where it usually locks on well.',
  'coach.zoneLandmark': 'Try heading toward {landmark} — tracking tends to lock on well there.',
  'coach.generic': 'Point the camera at a well-lit, detailed area — shelves, artwork or furniture work better than blank walls.',
  // 2D map view (en only — t() falls back).
  'ctrl.map2d': '2D map',
  'map2d.title': '🗺 Venue map',
  'map2d.viewBtn': '🗺 View venue map',
  'map2d.navigate': '🧭 Navigate',
  'map2d.close': 'Close',
  'map2d.away': '{d} m away',
  'map2d.hint': 'Drag to pan · pinch to zoom · tap a pin',
  'map2d.fallback': 'Camera lock lost — showing your route on the map',
  'map2d.noroute': 'No walkable path found — the map can show the direction instead.',
  'map2d.show': '🗺 Show map',
  'map2d.stayAr': 'Stay in AR',
  // First-run tutorial (en only — t() falls back).
  'tut.title': 'Quick tour',
  'tut.skip': 'Skip',
  'tut.next': 'Next',
  'tut.done': 'Got it',
  'tut.localize': 'Point your phone around the space — it locks on in a few seconds.',
  'tut.navigate': 'Tap 🧭, pick a destination, then follow the arrow and the discs on the floor.',
  'tut.concierge': 'Tap 💬 and just ask — like "take me to the office, then the kitchen".',
  'tut.voice': '🗣 turns spoken directions on or off.',
  'tut.map2d': '🗺 opens a 2D map of the venue with your live position.',
  'tut.explore': '🏷 shows floating labels on the places around you.',
  'tut.parking': '🚗 remembers where you parked — find it later under "Where to?".',
  'tut.capture': '📸 Arriving at marked places captures a short photo + video for your visit report.',
  'tut.more': 'Tap anywhere on screen for extra tools (voice, map, chat…) — they fade away again after a few seconds.',
  // 🏢 Facility reporting (en only — t() falls back).
  'fac.title': '🛠 Report an issue',
  'fac.category': 'What kind of issue?',
  'fac.notePh': 'Describe the issue (optional)…',
  'fac.namePh': 'Your name or desk (optional)',
  'fac.photo': '📷 Attach a photo of the spot',
  'fac.near': 'Location: near {name}',
  'fac.submit': 'Send report',
  'fac.sent': 'Reported — the facilities team has been notified.',
  // 🏢 Visitor check-in (en only — t() falls back).
  'chk.title': '🪪 Check in',
  'chk.namePh': 'Your name *',
  'chk.companyPh': 'Company (optional)',
  'chk.hostPh': 'Who are you here to see? (optional)',
  'chk.purposePh': 'Purpose of visit (optional)',
  'chk.submit': 'Check in',
  'chk.done': 'You’re checked in — the team has been notified.',
  'chk.checkedIn': '✓ Checked in',
  'chk.reception': '🧭 Take me to reception',
  // 🖼 Exhibit mode (en only — t() falls back).
  'exh.stop': 'Exhibit {n} of {total}',
  'exh.next': 'Next exhibit ▶',
  // 🏥 Appointments (en only — t() falls back).
  'appt.arrived': 'Appointment check-in complete — you’ve arrived at {name}.',
  // 🔤 Sign translation (en only — t() falls back).
  'tr.button': '🔤 Translate a sign',
  'tr.frame': 'Frame the sign, then tap ✓',
  'tr.reading': 'Reading the sign…',
  'tr.original': 'Original ({lang})',
  'tr.speak': '🔊 Read aloud',
  'tr.copy': '📋 Copy',
  'tr.copied': 'Copied.',
  'tr.blank': 'The camera frame looked empty — hold the phone steady and try again.',
  'tr.unreadable': 'Couldn’t read that sign — get closer or find better light.',
  'tr.error': 'Translation didn’t go through — try again.',
  'tr.capped': 'Sign translation is temporarily unavailable here — please ask venue staff.',
  // Common
  'common.cancel': 'Cancel',
};

const es: Dict = {
  'landing.explain':
    'Abre la cámara para iniciar la navegación AR en interiores. Apunta el teléfono alrededor del espacio para localizarte, elige un destino y sigue las flechas.',
  'landing.start': 'Iniciar AR',
  'landing.checking': 'Comprobando dispositivo…',
  'landing.connecting': 'Conectando…',
  'landing.ready': 'Listo. Asegúrate de estar dentro del espacio mapeado.',
  'state.notStarted': 'Sin iniciar',
  'state.permission': 'Se necesita permiso de cámara',
  'state.localizing': 'Localizando…',
  'state.localized': 'Localizado',
  'state.trackingWeak': 'Seguimiento débil',
  'state.lostTracking': 'Seguimiento perdido',
  'state.relocalizing': 'Relocalizando…',
  'state.error': 'Error',
  'hud.toNextTurn': '{d} hasta el próximo giro',
  'hud.straight': 'Sigue recto',
  'hud.turns': '{n} giros',
  'hud.turn1': '1 giro',
  'hud.eta': '≈{n} min hasta',
  'hud.floorUp': '⬆️ Sube por las escaleras',
  'hud.floorDown': '⬇️ Baja por las escaleras',
  'arrival.title': '🎉 ¡Has llegado!',
  'arrival.again': 'Ir a otro lugar',
  'route.none': 'No hay ruta a {name} — la navegación entre pisos necesita escaleras o ascensor.',
  'voice.start': 'Navegando a {name}, {m} metros',
  'voice.turn': 'Giro en {m} metros',
  'voice.arrived': 'Has llegado a {name}',
  'voice.updated': 'Ruta actualizada',
  'voice.stairsUp': 'Sube por las escaleras más adelante',
  'voice.stairsDown': 'Baja por las escaleras más adelante',
  'voice.on': 'Guía de voz activada',
  'tour.start': 'Iniciando recorrido: {name}, {n} paradas',
  'tour.stop': 'Parada {i} de {n}: {name}',
  'tour.complete': 'Recorrido completado',
  'tour.next': '✓ {name} · próxima parada en un momento…',
  'prox.passing': 'Estás pasando por {name}',
  'picker.whereTo': '¿A dónde vamos?',
  'picker.search': 'Buscar destinos…',
  'picker.stepFree': '♿ Ruta sin escalones',
  'picker.alerts': '🔔 Avisarme al pasar por lugares',
  'picker.tours': 'Recorridos guiados',
  'picker.dests': 'Destinos',
  'picker.none': 'Sin resultados.',
  'picker.stops': '{n} paradas',
  'ctrl.relocalize': 'Relocalizar',
  'ctrl.change': 'Cambiar destino',
  'ctrl.voice': 'Guía de voz',
  'ctrl.stop': 'Detener navegación',
  'loc.noPose': 'Sin posición — apunta la cámara a una zona mapeada con detalles',
  'loc.lowConf': 'Confianza baja ({c}) — apunta a una zona distintiva y bien iluminada',
  'link.open': 'Abrir en el navegador',
  'ctrl.explore': 'Explorar lugares',
  'ctrl.park': 'Guardar aparcamiento',
  'park.saved': 'Aparcamiento guardado — está en «¿A dónde vamos?»',
  'park.mySpot': 'Mi aparcamiento',
  'ctrl.concierge': 'Preguntar al conserje',
  'concierge.title': 'Pregunta a NavMe',
  'concierge.placeholder': 'p. ej. «¿baño más cercano?»',
  'concierge.thinking': 'Pensando…',
  'concierge.go': 'Llévame allí',
};

const hi: Dict = {
  'landing.explain':
    'इनडोर AR नेविगेशन शुरू करने के लिए कैमरा खोलें। लोकेशन पहचान के लिए फ़ोन को चारों ओर घुमाएँ, गंतव्य चुनें और तीरों का पालन करें।',
  'landing.start': 'AR शुरू करें',
  'landing.checking': 'डिवाइस जाँच रहे हैं…',
  'landing.connecting': 'कनेक्ट हो रहा है…',
  'landing.ready': 'तैयार। सुनिश्चित करें कि आप मैप किए गए स्थान के अंदर हैं।',
  'state.notStarted': 'शुरू नहीं हुआ',
  'state.permission': 'कैमरा अनुमति चाहिए',
  'state.localizing': 'स्थान पहचान रहा है…',
  'state.localized': 'स्थान मिल गया',
  'state.trackingWeak': 'ट्रैकिंग कमज़ोर',
  'state.lostTracking': 'ट्रैकिंग खो गई',
  'state.relocalizing': 'फिर से पहचान रहा है…',
  'state.error': 'त्रुटि',
  'hud.toNextTurn': 'अगले मोड़ तक {d}',
  'hud.straight': 'सीधे चलें',
  'hud.turns': '{n} मोड़',
  'hud.turn1': '1 मोड़',
  'hud.eta': '≈{n} मिनट,',
  'hud.floorUp': '⬆️ सीढ़ियों से ऊपर जाएँ',
  'hud.floorDown': '⬇️ सीढ़ियों से नीचे जाएँ',
  'arrival.title': '🎉 आप पहुँच गए!',
  'arrival.again': 'कहीं और जाएँ',
  'route.none': '{name} तक कोई रास्ता नहीं — मंज़िलों के बीच सीढ़ी/लिफ़्ट चाहिए।',
  'voice.start': '{name} की ओर जा रहे हैं, {m} मीटर',
  'voice.turn': '{m} मीटर में मोड़ है',
  'voice.arrived': 'आप {name} पहुँच गए हैं',
  'voice.updated': 'रास्ता अपडेट हुआ',
  'voice.stairsUp': 'आगे सीढ़ियों से ऊपर जाएँ',
  'voice.stairsDown': 'आगे सीढ़ियों से नीचे जाएँ',
  'voice.on': 'वॉइस गाइड चालू',
  'tour.start': 'टूर शुरू: {name}, {n} पड़ाव',
  'tour.stop': 'पड़ाव {i}/{n}: {name}',
  'tour.complete': 'टूर पूरा हुआ',
  'tour.next': '✓ {name} · अगला पड़ाव कुछ ही पल में…',
  'prox.passing': 'आप {name} के पास से गुज़र रहे हैं',
  'picker.whereTo': 'कहाँ जाना है?',
  'picker.search': 'गंतव्य खोजें…',
  'picker.stepFree': '♿ बिना सीढ़ी वाला रास्ता',
  'picker.alerts': '🔔 पास से गुज़रने पर बताएं',
  'picker.tours': 'गाइडेड टूर',
  'picker.dests': 'गंतव्य',
  'picker.none': 'कोई परिणाम नहीं।',
  'picker.stops': '{n} पड़ाव',
  'ctrl.relocalize': 'फिर से लोकेट करें',
  'ctrl.change': 'गंतव्य बदलें',
  'ctrl.voice': 'वॉइस गाइड',
  'ctrl.stop': 'नेविगेशन रोकें',
  'loc.noPose': 'स्थान नहीं मिला — कैमरा मैप किए गए, विशिष्ट क्षेत्र की ओर करें',
  'loc.lowConf': 'कम विश्वसनीयता ({c}) — किसी विशिष्ट, रोशनी वाले क्षेत्र की ओर करें',
  'link.open': 'ब्राउज़र में खोलें',
  'ctrl.explore': 'स्थान देखें',
  'ctrl.park': 'पार्किंग सेव करें',
  'park.saved': 'पार्किंग सेव हुई — “कहाँ जाना है?” में देखें',
  'park.mySpot': 'मेरी पार्किंग',
  'ctrl.concierge': 'कंसीयज से पूछें',
  'concierge.title': 'NavMe से पूछें',
  'concierge.placeholder': 'जैसे “सबसे पास का शौचालय?”',
  'concierge.thinking': 'सोच रहा हूँ…',
  'concierge.go': 'मुझे वहाँ ले चलो',
};

const te: Dict = {
  'landing.explain':
    'ఇండోర్ AR నావిగేషన్ ప్రారంభించడానికి కెమెరా తెరవండి. లొకేషన్ గుర్తించడానికి ఫోన్‌ను చుట్టూ చూపించి, గమ్యాన్ని ఎంచుకుని, బాణాలను అనుసరించండి.',
  'landing.start': 'AR ప్రారంభించండి',
  'landing.checking': 'పరికరాన్ని తనిఖీ చేస్తోంది…',
  'landing.connecting': 'కనెక్ట్ అవుతోంది…',
  'landing.ready': 'సిద్ధం. మీరు మ్యాప్ చేసిన ప్రదేశంలో ఉన్నారని నిర్ధారించుకోండి.',
  'state.notStarted': 'ప్రారంభం కాలేదు',
  'state.permission': 'కెమెరా అనుమతి అవసరం',
  'state.localizing': 'స్థానం గుర్తిస్తోంది…',
  'state.localized': 'స్థానం గుర్తించబడింది',
  'state.trackingWeak': 'ట్రాకింగ్ బలహీనం',
  'state.lostTracking': 'ట్రాకింగ్ కోల్పోయింది',
  'state.relocalizing': 'మళ్లీ గుర్తిస్తోంది…',
  'state.error': 'లోపం',
  'hud.toNextTurn': 'తదుపరి మలుపుకు {d}',
  'hud.straight': 'నేరుగా వెళ్లండి',
  'hud.turns': '{n} మలుపులు',
  'hud.turn1': '1 మలుపు',
  'hud.eta': '≈{n} నిమి,',
  'hud.floorUp': '⬆️ మెట్ల మీదుగా పైకి వెళ్లండి',
  'hud.floorDown': '⬇️ మెట్ల మీదుగా కిందికి వెళ్లండి',
  'arrival.title': '🎉 మీరు చేరుకున్నారు!',
  'arrival.again': 'మరో చోటికి వెళ్లండి',
  'route.none': '{name}కి దారి లేదు — అంతస్తుల మధ్య మెట్లు/లిఫ్ట్ అవసరం.',
  'voice.start': '{name} వైపు వెళ్తున్నాం, {m} మీటర్లు',
  'voice.turn': '{m} మీటర్లలో మలుపు ఉంది',
  'voice.arrived': 'మీరు {name} చేరుకున్నారు',
  'voice.updated': 'దారి నవీకరించబడింది',
  'voice.stairsUp': 'ముందు మెట్ల మీదుగా పైకి వెళ్లండి',
  'voice.stairsDown': 'ముందు మెట్ల మీదుగా కిందికి వెళ్లండి',
  'voice.on': 'వాయిస్ గైడ్ ఆన్',
  'tour.start': 'టూర్ ప్రారంభం: {name}, {n} మజిలీలు',
  'tour.stop': 'మజిలీ {i}/{n}: {name}',
  'tour.complete': 'టూర్ పూర్తయింది',
  'tour.next': '✓ {name} · తదుపరి మజిలీ కాసేపట్లో…',
  'prox.passing': 'మీరు {name} పక్కగా వెళ్తున్నారు',
  'picker.whereTo': 'ఎక్కడికి వెళ్లాలి?',
  'picker.search': 'గమ్యాలను వెతకండి…',
  'picker.stepFree': '♿ మెట్లు లేని దారి',
  'picker.alerts': '🔔 స్థలాల పక్కగా వెళ్లేటప్పుడు తెలియజేయి',
  'picker.tours': 'గైడెడ్ టూర్లు',
  'picker.dests': 'గమ్యాలు',
  'picker.none': 'ఫలితాలు లేవు.',
  'picker.stops': '{n} మజిలీలు',
  'ctrl.relocalize': 'మళ్లీ గుర్తించు',
  'ctrl.change': 'గమ్యం మార్చు',
  'ctrl.voice': 'వాయిస్ గైడ్',
  'ctrl.stop': 'నావిగేషన్ ఆపు',
  'loc.noPose': 'స్థానం దొరకలేదు — కెమెరాను మ్యాప్ చేసిన, ప్రత్యేకమైన ప్రాంతం వైపు చూపించండి',
  'loc.lowConf': 'తక్కువ విశ్వసనీయత ({c}) — ప్రత్యేకమైన, వెలుతురు ఉన్న ప్రాంతం వైపు చూపించండి',
  'link.open': 'బ్రౌజర్‌లో తెరవండి',
  'ctrl.explore': 'ప్రదేశాలు చూడండి',
  'ctrl.park': 'పార్కింగ్ సేవ్ చేయండి',
  'park.saved': 'పార్కింగ్ సేవ్ అయింది — “ఎక్కడికి వెళ్లాలి?”లో చూడండి',
  'park.mySpot': 'నా పార్కింగ్',
  'ctrl.concierge': 'కన్సియర్జ్‌ని అడగండి',
  'concierge.title': 'NavMeని అడగండి',
  'concierge.placeholder': 'ఉదా. “దగ్గరి వాష్‌రూమ్?”',
  'concierge.thinking': 'ఆలోచిస్తున్నాను…',
  'concierge.go': 'నన్ను అక్కడికి తీసుకెళ్లు',
};

const fr: Dict = {
  'landing.explain':
    "Ouvrez la caméra pour démarrer la navigation AR en intérieur. Balayez l'espace avec votre téléphone pour vous localiser, choisissez une destination et suivez les flèches.",
  'landing.start': 'Démarrer AR',
  'landing.checking': "Vérification de l'appareil…",
  'landing.connecting': 'Connexion…',
  'landing.ready': "Prêt. Assurez-vous d'être dans l'espace cartographié.",
  'state.notStarted': 'Non démarré',
  'state.permission': 'Autorisation caméra requise',
  'state.localizing': 'Localisation…',
  'state.localized': 'Localisé',
  'state.trackingWeak': 'Suivi faible',
  'state.lostTracking': 'Suivi perdu',
  'state.relocalizing': 'Relocalisation…',
  'state.error': 'Erreur',
  'hud.toNextTurn': '{d} jusqu\u2019au prochain virage',
  'hud.straight': 'Tout droit',
  'hud.turns': '{n} virages',
  'hud.turn1': '1 virage',
  'hud.eta': '≈{n} min vers',
  'hud.floorUp': '⬆️ Montez les escaliers',
  'hud.floorDown': '⬇️ Descendez les escaliers',
  'arrival.title': '🎉 Vous êtes arrivé !',
  'arrival.again': 'Aller ailleurs',
  'route.none': "Pas d'itinéraire vers {name} — il faut des escaliers/ascenseur entre les étages.",
  'voice.start': 'Navigation vers {name}, {m} mètres',
  'voice.turn': 'Virage dans {m} mètres',
  'voice.arrived': 'Vous êtes arrivé à {name}',
  'voice.updated': 'Itinéraire mis à jour',
  'voice.stairsUp': 'Montez les escaliers devant vous',
  'voice.stairsDown': 'Descendez les escaliers devant vous',
  'voice.on': 'Guidage vocal activé',
  'tour.start': 'Début de la visite : {name}, {n} étapes',
  'tour.stop': 'Étape {i} sur {n} : {name}',
  'tour.complete': 'Visite terminée',
  'tour.next': '✓ {name} · prochaine étape dans un instant…',
  'prox.passing': 'Vous passez devant {name}',
  'picker.whereTo': 'Où aller ?',
  'picker.search': 'Rechercher une destination…',
  'picker.stepFree': '♿ Itinéraire sans marches',
  'picker.alerts': '🔔 Me signaler les lieux à proximité',
  'picker.tours': 'Visites guidées',
  'picker.dests': 'Destinations',
  'picker.none': 'Aucun résultat.',
  'picker.stops': '{n} étapes',
  'ctrl.relocalize': 'Relocaliser',
  'ctrl.change': 'Changer de destination',
  'ctrl.voice': 'Guidage vocal',
  'ctrl.stop': 'Arrêter la navigation',
  'loc.noPose': 'Position introuvable — visez une zone cartographiée et riche en détails',
  'loc.lowConf': 'Confiance faible ({c}) — visez une zone distinctive et bien éclairée',
  'link.open': 'Ouvrir dans le navigateur',
  'ctrl.explore': 'Explorer les lieux',
  'ctrl.park': 'Enregistrer ma place',
  'park.saved': 'Place enregistrée — voir « Où aller ? »',
  'park.mySpot': 'Ma place de parking',
  'ctrl.concierge': 'Demander au concierge',
  'concierge.title': 'Demandez à NavMe',
  'concierge.placeholder': 'ex. « toilettes les plus proches ? »',
  'concierge.thinking': 'Je réfléchis…',
  'concierge.go': 'Emmène-moi',
};

const de: Dict = {
  'landing.explain':
    'Öffnen Sie die Kamera, um die Indoor-AR-Navigation zu starten. Schwenken Sie das Telefon durch den Raum zur Ortung, wählen Sie ein Ziel und folgen Sie den Pfeilen.',
  'landing.start': 'AR starten',
  'landing.checking': 'Gerät wird geprüft…',
  'landing.connecting': 'Verbinden…',
  'landing.ready': 'Bereit. Stellen Sie sicher, dass Sie sich im kartierten Bereich befinden.',
  'state.notStarted': 'Nicht gestartet',
  'state.permission': 'Kameraberechtigung erforderlich',
  'state.localizing': 'Ortung läuft…',
  'state.localized': 'Geortet',
  'state.trackingWeak': 'Tracking schwach',
  'state.lostTracking': 'Tracking verloren',
  'state.relocalizing': 'Neuortung…',
  'state.error': 'Fehler',
  'hud.toNextTurn': '{d} bis zur nächsten Abzweigung',
  'hud.straight': 'Geradeaus',
  'hud.turns': '{n} Abzweigungen',
  'hud.turn1': '1 Abzweigung',
  'hud.eta': '≈{n} Min. bis',
  'hud.floorUp': '⬆️ Die Treppe hinauf',
  'hud.floorDown': '⬇️ Die Treppe hinunter',
  'arrival.title': '🎉 Sie sind angekommen!',
  'arrival.again': 'Woanders hin navigieren',
  'route.none': 'Keine Route zu {name} — zwischen Etagen sind Treppen/Aufzug nötig.',
  'voice.start': 'Navigation zu {name}, {m} Meter',
  'voice.turn': 'Abzweigung in {m} Metern',
  'voice.arrived': 'Sie haben {name} erreicht',
  'voice.updated': 'Route aktualisiert',
  'voice.stairsUp': 'Nehmen Sie die Treppe nach oben',
  'voice.stairsDown': 'Nehmen Sie die Treppe nach unten',
  'voice.on': 'Sprachführung an',
  'tour.start': 'Tour startet: {name}, {n} Stationen',
  'tour.stop': 'Station {i} von {n}: {name}',
  'tour.complete': 'Tour abgeschlossen',
  'tour.next': '✓ {name} · nächste Station gleich…',
  'prox.passing': 'Sie passieren {name}',
  'picker.whereTo': 'Wohin?',
  'picker.search': 'Ziele suchen…',
  'picker.stepFree': '♿ Stufenfreie Route',
  'picker.alerts': '🔔 Beim Passieren von Orten benachrichtigen',
  'picker.tours': 'Geführte Touren',
  'picker.dests': 'Ziele',
  'picker.none': 'Keine Treffer.',
  'picker.stops': '{n} Stationen',
  'ctrl.relocalize': 'Neu orten',
  'ctrl.change': 'Ziel ändern',
  'ctrl.voice': 'Sprachführung',
  'ctrl.stop': 'Navigation beenden',
  'loc.noPose': 'Keine Position — richten Sie die Kamera auf einen kartierten, detailreichen Bereich',
  'loc.lowConf': 'Geringe Konfidenz ({c}) — auf einen markanten, gut beleuchteten Bereich richten',
  'link.open': 'Im Browser öffnen',
  'ctrl.explore': 'Orte erkunden',
  'ctrl.park': 'Parkplatz merken',
  'park.saved': 'Parkplatz gespeichert — unter „Wohin?\u201c zu finden',
  'park.mySpot': 'Mein Parkplatz',
  'ctrl.concierge': 'Concierge fragen',
  'concierge.title': 'Frag NavMe',
  'concierge.placeholder': 'z. B. \u201enächste Toilette?\u201c',
  'concierge.thinking': 'Denke nach…',
  'concierge.go': 'Bring mich hin',
};

const pt: Dict = {
  'landing.explain':
    'Abra a câmera para iniciar a navegação AR em ambientes internos. Aponte o telefone ao redor para se localizar, escolha um destino e siga as setas.',
  'landing.start': 'Iniciar AR',
  'landing.checking': 'Verificando dispositivo…',
  'landing.connecting': 'Conectando…',
  'landing.ready': 'Pronto. Certifique-se de estar dentro do espaço mapeado.',
  'state.notStarted': 'Não iniciado',
  'state.permission': 'Permissão de câmera necessária',
  'state.localizing': 'Localizando…',
  'state.localized': 'Localizado',
  'state.trackingWeak': 'Rastreamento fraco',
  'state.lostTracking': 'Rastreamento perdido',
  'state.relocalizing': 'Relocalizando…',
  'state.error': 'Erro',
  'hud.toNextTurn': '{d} até a próxima curva',
  'hud.straight': 'Siga em frente',
  'hud.turns': '{n} curvas',
  'hud.turn1': '1 curva',
  'hud.eta': '≈{n} min até',
  'hud.floorUp': '⬆️ Suba as escadas',
  'hud.floorDown': '⬇️ Desça as escadas',
  'arrival.title': '🎉 Você chegou!',
  'arrival.again': 'Ir para outro lugar',
  'route.none': 'Sem rota para {name} — entre andares são necessárias escadas/elevador.',
  'voice.start': 'Navegando para {name}, {m} metros',
  'voice.turn': 'Curva em {m} metros',
  'voice.arrived': 'Você chegou a {name}',
  'voice.updated': 'Rota atualizada',
  'voice.stairsUp': 'Suba as escadas à frente',
  'voice.stairsDown': 'Desça as escadas à frente',
  'voice.on': 'Guia de voz ativado',
  'tour.start': 'Iniciando tour: {name}, {n} paradas',
  'tour.stop': 'Parada {i} de {n}: {name}',
  'tour.complete': 'Tour concluído',
  'tour.next': '✓ {name} · próxima parada em instantes…',
  'prox.passing': 'Você está passando por {name}',
  'picker.whereTo': 'Para onde?',
  'picker.search': 'Buscar destinos…',
  'picker.stepFree': '♿ Rota sem degraus',
  'picker.alerts': '🔔 Avisar ao passar por lugares',
  'picker.tours': 'Tours guiados',
  'picker.dests': 'Destinos',
  'picker.none': 'Nenhum resultado.',
  'picker.stops': '{n} paradas',
  'ctrl.relocalize': 'Relocalizar',
  'ctrl.change': 'Mudar destino',
  'ctrl.voice': 'Guia de voz',
  'ctrl.stop': 'Parar navegação',
  'loc.noPose': 'Posição não encontrada — aponte a câmera para uma área mapeada e rica em detalhes',
  'loc.lowConf': 'Confiança baixa ({c}) — aponte para uma área distinta e bem iluminada',
  'link.open': 'Abrir no navegador',
  'ctrl.explore': 'Explorar lugares',
  'ctrl.park': 'Salvar vaga',
  'park.saved': 'Vaga salva — veja em “Para onde?”',
  'park.mySpot': 'Minha vaga',
  'ctrl.concierge': 'Perguntar ao concierge',
  'concierge.title': 'Pergunte ao NavMe',
  'concierge.placeholder': 'ex.: “banheiro mais próximo?”',
  'concierge.thinking': 'Pensando…',
  'concierge.go': 'Leve-me até lá',
};

const zh: Dict = {
  'landing.explain': '打开相机开始室内AR导航。将手机对准四周进行定位，选择目的地，然后跟随箭头前进。',
  'landing.start': '开始 AR',
  'landing.checking': '正在检查设备…',
  'landing.connecting': '连接中…',
  'landing.ready': '准备就绪。请确保您位于已建图的空间内。',
  'state.notStarted': '未开始',
  'state.permission': '需要相机权限',
  'state.localizing': '定位中…',
  'state.localized': '已定位',
  'state.trackingWeak': '追踪较弱',
  'state.lostTracking': '追踪丢失',
  'state.relocalizing': '重新定位中…',
  'state.error': '错误',
  'hud.toNextTurn': '距下个转弯 {d}',
  'hud.straight': '直行',
  'hud.turns': '{n} 个转弯',
  'hud.turn1': '1 个转弯',
  'hud.eta': '约{n}分钟到',
  'hud.floorUp': '⬆️ 走楼梯上楼',
  'hud.floorDown': '⬇️ 走楼梯下楼',
  'arrival.title': '🎉 您已到达！',
  'arrival.again': '前往其他地方',
  'route.none': '无法到达{name} — 跨楼层需要楼梯或电梯。',
  'voice.start': '正在导航至{name}，{m}米',
  'voice.turn': '{m}米后转弯',
  'voice.arrived': '您已到达{name}',
  'voice.updated': '路线已更新',
  'voice.stairsUp': '前方请走楼梯上楼',
  'voice.stairsDown': '前方请走楼梯下楼',
  'voice.on': '语音导航已开启',
  'tour.start': '开始游览：{name}，共{n}站',
  'tour.stop': '第{i}站，共{n}站：{name}',
  'tour.complete': '游览结束',
  'tour.next': '✓ {name} · 即将前往下一站…',
  'prox.passing': '您正经过{name}',
  'picker.whereTo': '去哪里？',
  'picker.search': '搜索目的地…',
  'picker.stepFree': '♿ 无台阶路线',
  'picker.alerts': '🔔 经过地点时提醒我',
  'picker.tours': '导览路线',
  'picker.dests': '目的地',
  'picker.none': '无结果。',
  'picker.stops': '{n} 站',
  'ctrl.relocalize': '重新定位',
  'ctrl.change': '更改目的地',
  'ctrl.voice': '语音导航',
  'ctrl.stop': '停止导航',
  'loc.noPose': '未找到位置 — 请将相机对准已建图、细节丰富的区域',
  'loc.lowConf': '置信度低（{c}）— 请对准有特色、光线好的区域',
  'link.open': '在浏览器中打开',
  'ctrl.explore': '探索地点',
  'ctrl.park': '保存停车位',
  'park.saved': '车位已保存 — 在“去哪里？”中查看',
  'park.mySpot': '我的车位',
  'ctrl.concierge': '询问礼宾',
  'concierge.title': '问问 NavMe',
  'concierge.placeholder': '例如“最近的洗手间？”',
  'concierge.thinking': '思考中…',
  'concierge.go': '带我去',
};

const ja: Dict = {
  'landing.explain': 'カメラを開いて屋内ARナビを開始します。スマホを周囲に向けて位置を特定し、目的地を選んで矢印に従ってください。',
  'landing.start': 'ARを開始',
  'landing.checking': 'デバイスを確認中…',
  'landing.connecting': '接続中…',
  'landing.ready': '準備完了。マッピング済みの空間内にいることを確認してください。',
  'state.notStarted': '未開始',
  'state.permission': 'カメラの許可が必要です',
  'state.localizing': '位置特定中…',
  'state.localized': '位置特定済み',
  'state.trackingWeak': 'トラッキング弱',
  'state.lostTracking': 'トラッキング喪失',
  'state.relocalizing': '再特定中…',
  'state.error': 'エラー',
  'hud.toNextTurn': '次の曲がり角まで {d}',
  'hud.straight': '直進',
  'hud.turns': '曲がり角 {n} 回',
  'hud.turn1': '曲がり角 1 回',
  'hud.eta': '約{n}分で',
  'hud.floorUp': '⬆️ 階段を上ってください',
  'hud.floorDown': '⬇️ 階段を下りてください',
  'arrival.title': '🎉 到着しました！',
  'arrival.again': '別の場所へ',
  'route.none': '{name}へのルートがありません — 階をまたぐには階段/エレベーターが必要です。',
  'voice.start': '{name}へ案内します。{m}メートルです',
  'voice.turn': '{m}メートル先で曲がります',
  'voice.arrived': '{name}に到着しました',
  'voice.updated': 'ルートを更新しました',
  'voice.stairsUp': 'この先、階段を上ってください',
  'voice.stairsDown': 'この先、階段を下りてください',
  'voice.on': '音声ガイドをオンにしました',
  'tour.start': 'ツアー開始：{name}、全{n}か所',
  'tour.stop': '{n}か所中{i}か所目：{name}',
  'tour.complete': 'ツアー終了',
  'tour.next': '✓ {name} · まもなく次のスポットへ…',
  'prox.passing': '{name}の近くを通過中です',
  'picker.whereTo': 'どちらへ？',
  'picker.search': '目的地を検索…',
  'picker.stepFree': '♿ 段差のないルート',
  'picker.alerts': '🔔 近くの場所を通ったら知らせる',
  'picker.tours': 'ガイドツアー',
  'picker.dests': '目的地',
  'picker.none': '該当なし。',
  'picker.stops': '{n} か所',
  'ctrl.relocalize': '再位置特定',
  'ctrl.change': '目的地を変更',
  'ctrl.voice': '音声ガイド',
  'ctrl.stop': 'ナビを停止',
  'loc.noPose': '位置が見つかりません — マッピング済みの特徴的なエリアにカメラを向けてください',
  'loc.lowConf': '信頼度が低い（{c}）— 特徴的で明るいエリアに向けてください',
  'link.open': 'ブラウザで開く',
  'ctrl.explore': 'スポットを見る',
  'ctrl.park': '駐車位置を保存',
  'park.saved': '保存しました — 「どちらへ？」から確認できます',
  'park.mySpot': 'マイ駐車位置',
  'ctrl.concierge': 'コンシェルジュに質問',
  'concierge.title': 'NavMeに聞く',
  'concierge.placeholder': '例：「一番近いトイレは？」',
  'concierge.thinking': '考え中…',
  'concierge.go': '案内して',
};

const DICTS: Record<Lang, Dict> = { en, es, hi, te, fr, de, pt, zh, ja };
